import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { stockLocationsRestCrudThunksForSupplier } from '../../thunks/stock_locations_thunks';
import { connect } from 'react-redux';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import { Popover, Select } from 'antd';
import { StockLocationDropdownRecord } from '../StockLocationDropdownRecord';
import { getRecordsForCompositeTargetCollection } from '../../reducers/standard_reducer_utils';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { isForwardingAllowedBySettings } from '../../utils/InventoryUtils';

const SL_TC_NAME = 'viaStockLocationIndex';

const StagingSLDropDown: FC<any> = ({
	onChange,
	purchaseOrder,

	stockLocations,
	fetchMultipleStockLocations,
	fetchStockLocations,
	companySettings,
}): React.ReactElement => {
	const [selectedValue, setSelectedValue] = useState(undefined);
	const hasInitialized = useRef(false);

	const isNewPO = useMemo(() => !nullSafeGet('id', purchaseOrder), [purchaseOrder]);

	const equipmentLineItems = useMemo(
		() => nullSafeGetOrElse('equipmentSupplierPurchaseOrderLineItems', purchaseOrder, []),
		[purchaseOrder]
	);

	const isLocationPO = useMemo(() => !!nullSafeGet('locationId', purchaseOrder), [purchaseOrder]);

	useEffect(() => {
		if (!isLocationPO || !isNewPO || selectedValue || hasInitialized.current) {
			return;
		}

		const slRecords = getRecordsForCompositeTargetCollection(stockLocations, SL_TC_NAME);
		const firstSLRecord = nullSafeGet('0', slRecords);
		if (firstSLRecord) {
			hasInitialized.current = true;
			setSelectedValue(firstSLRecord.id);
			onChange(firstSLRecord);
		}
	}, [isLocationPO, isNewPO, onChange, selectedValue, stockLocations]);

	const onSLChange = useCallback(
		(slId) => {
			const slRecords = getRecordsForCompositeTargetCollection(stockLocations, SL_TC_NAME);
			const newSL = slId ? slRecords.find((_) => _.id === slId) : undefined;
			setSelectedValue(slId);
			onChange(newSL);
		},
		[onChange, stockLocations]
	);

	const canShowStaging = useMemo(
		() =>
			isLocationPO &&
			nullSafeGet('0', equipmentLineItems) &&
			isForwardingAllowedBySettings(companySettings),
		[companySettings, equipmentLineItems, isLocationPO]
	);

	const someLineItemsReceived = useMemo(
		() => equipmentLineItems.some((_) => nullSafeGetOrElse('equipmentReceivedQuantity', _, 0) > 0),
		[equipmentLineItems]
	);

	const commonViaSLId = useMemo(() => {
		const allViaSLIds = equipmentLineItems.map((_) => _.viaStockLocationId);
		const first = allViaSLIds[0];
		const allSame = allViaSLIds.every((id) => id === first);

		return allSame ? first : undefined;
	}, [equipmentLineItems]);

	const someLineItemHasViaSLId = useMemo(
		() => equipmentLineItems.some((_) => !!nullSafeGet('viaStockLocationId', _)),
		[equipmentLineItems]
	);

	const disabledMessage = useMemo(() => {
		const someHasViaSLIdAndAreNotEqual = someLineItemHasViaSLId && !commonViaSLId;
		return someLineItemsReceived
			? 'Some items are already received. Edit non received line items individually'
			: someHasViaSLIdAndAreNotEqual
			? 'Forwarding stock locations are different for line items'
			: null;
	}, [commonViaSLId, someLineItemHasViaSLId, someLineItemsReceived]);

	useEffect(() => {
		if (commonViaSLId) {
			setSelectedValue(commonViaSLId);
		}
	}, [commonViaSLId]);

	return canShowStaging ? (
		<div className="flex flex-row items-center gap-2">
			<div>Forward Via</div>
			<div style={{ minWidth: 200 }}>
				<Popover content={disabledMessage} trigger="hover">
					<OWAsyncSelect
						stateSlice={stockLocations}
						disabled={!!disabledMessage}
						onChange={onSLChange}
						value={selectedValue}
						allowClear
						targetCollectionName={SL_TC_NAME}
						fetchMultiple={(ids, targetCollectionName) => {
							fetchMultipleStockLocations(ids, targetCollectionName);
						}}
						fetchData={(
							searchText,
							targetCollectionName,
							pagination,
							sorting,
							filters,
							addToTargetCollection
						) => {
							fetchStockLocations(
								{ name: searchText },
								targetCollectionName,
								pagination,
								sorting,
								filters,
								addToTargetCollection
							);
						}}
						renderRecord={(record) => (
							<Select.Option key={`${record.id} - ${record.name}`} value={record.id}>
								<StockLocationDropdownRecord record={record} />
							</Select.Option>
						)}
					/>
				</Popover>
			</div>
		</div>
	) : null;
};

const mapStateToProps = (state) => ({
	stockLocations: state.stock_locations,
	companySettings: state.company_config.detail,
});

const mapDispatchToProps = (dispatch) => ({
	fetchStockLocations: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			stockLocationsRestCrudThunksForSupplier.readLite(
				{ ...params, allowEquipmentForwarding: true, noIsActiveFilter: false },
				targetCollectionName,
				pagination,
				sorting || { sort_by: 'name', order: 'ascend' },
				filters,
				addToTargetCollection
			)
		),
	fetchMultipleStockLocations: (ids, targetCollectionName) =>
		dispatch(stockLocationsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
});

export default connect(mapStateToProps, mapDispatchToProps)(StagingSLDropDown);
